import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";

import { ICreditTransaction } from "../../types/creditTransaction";
import { IUrbanPartnerSimple } from "../../types/seederOrders";
import useUrbanPartners from "../../services/useUrbanPartners";
import { useDebounce } from "../../hooks/useDebounce";

export interface ITransactionFormData {
  urban_partner_id: number | null;
  amount: string;
  description: string;
}

interface TransactionDialogProps {
  open: boolean;
  editingTransaction: ICreditTransaction | null;
  formData: ITransactionFormData;
  isLoading: boolean;
  onClose: () => void;
  onSave: () => void;
  onFormDataChange: (formData: ITransactionFormData) => void;
}

const TransactionDialog: FC<TransactionDialogProps> = ({
  open,
  editingTransaction,
  formData,
  isLoading,
  onClose,
  onSave,
  onFormDataChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedUrbanPartner, setSelectedUrbanPartner] =
    useState<IUrbanPartnerSimple | null>(null);
  const debouncedSearch = useDebounce(inputValue, 300);
  const { data, isLoading: isUrbanPartnersLoading } = useUrbanPartners({
    search: debouncedSearch,
  });
  const fetchedUrbanPartners = useMemo(
    () => data?.results || [],
    [data?.results],
  );

  useEffect(() => {
    if (!formData.urban_partner_id) {
      setSelectedUrbanPartner(null);
      return;
    }

    const matched = fetchedUrbanPartners.find(
      (up) => up.id === formData.urban_partner_id,
    );
    if (matched) {
      setSelectedUrbanPartner(matched);
    }
  }, [fetchedUrbanPartners, formData.urban_partner_id]);

  const options = useMemo(() => {
    if (!selectedUrbanPartner) {
      return fetchedUrbanPartners;
    }

    const hasSelected = fetchedUrbanPartners.some(
      (partner) => partner.id === selectedUrbanPartner.id,
    );

    if (hasSelected) {
      return fetchedUrbanPartners;
    }

    return [selectedUrbanPartner, ...fetchedUrbanPartners];
  }, [fetchedUrbanPartners, selectedUrbanPartner]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingTransaction ? "Editar Transação" : "Novo Pré-Pagamento"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {!editingTransaction ? (
            <Autocomplete
              options={options}
              getOptionLabel={(option: IUrbanPartnerSimple) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(items) => items}
              loading={isUrbanPartnersLoading}
              inputValue={inputValue}
              onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
              value={selectedUrbanPartner || null}
              onChange={(_, newValue) => {
                setSelectedUrbanPartner(newValue);
                onFormDataChange({
                  ...formData,
                  urban_partner_id: newValue?.id || null,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parceira Urbana"
                  required
                  helperText="Selecione a parceira que receberá o crédito"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isUrbanPartnersLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          ) : (
            <TextField
              label="Parceira Urbana"
              value={editingTransaction.urban_partner_name}
              disabled
              fullWidth
            />
          )}
          <TextField
            label="Valor"
            type="number"
            value={formData.amount}
            onChange={(e) =>
              onFormDataChange({ ...formData, amount: e.target.value })
            }
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">R$</InputAdornment>
              ),
            }}
            inputProps={{ min: 0.01, step: "0.01" }}
            helperText="Valor do crédito a ser adicionado"
          />
          <TextField
            label="Descrição"
            value={formData.description}
            onChange={(e) =>
              onFormDataChange({ ...formData, description: e.target.value })
            }
            multiline
            rows={3}
            fullWidth
            placeholder="Ex: Pré-pagamento referente ao mês de Janeiro"
            helperText="Opcional - descreva o motivo do crédito"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={onSave} variant="contained" disabled={isLoading}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : editingTransaction ? (
            "Salvar"
          ) : (
            "Registrar Pré-Pagamento"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDialog;
